import { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Store, ArrowRight, Sparkles, ClipboardCheck, AlertTriangle } from 'lucide-react';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import {
    Tooltip,
    TooltipContent,
    TooltipProvider,
    TooltipTrigger,
} from '@/components/ui/tooltip';
import { PageHeader } from '../components/PageHeader';
import { PeriodPicker, useSharedPeriod, periodToParams } from '../components/PeriodPicker';
import { api, fmtDateTime, fmtMoney } from '../lib/api';

/**
 * Conservative "looks similar" detector.
 *
 * Matches are declared only when the evidence is strong:
 *   1. One key is a substring of the other AND the shorter has >= 6 chars.
 *   2. Same-token-prefix: they share first two whitespace tokens and one
 *      extends the other (e.g. "shiv gauri agencies" vs
 *      "shiv gauri agencies jodhpur").
 *   3. One key equals the other with all whitespace removed AND is >= 6 chars
 *      (catches OCR variants like "shivgauri agencies" vs "shiv gauri agencies").
 *
 * Returns a Map<vendor_key, otherVendorObject> (first match wins per row).
 */
function computeSimilar(rows) {
    const map = new Map();
    if (!rows || rows.length < 2) return map;

    const norms = rows.map((r) => {
        const k = (r.vendor_key || '').trim();
        return {
            ...r,
            _k: k,
            _tokens: k.split(/\s+/).filter(Boolean),
            _collapsed: k.replace(/\s+/g, ''),
        };
    });

    for (let i = 0; i < norms.length; i += 1) {
        const a = norms[i];
        if (!a._k) continue;
        for (let j = 0; j < norms.length; j += 1) {
            if (i === j) continue;
            const b = norms[j];
            if (!b._k) continue;

            const shorter = a._k.length <= b._k.length ? a._k : b._k;
            const longer = a._k.length > b._k.length ? a._k : b._k;

            let hit = false;
            // Rule 1: substring containment (min 6 chars for the shorter one)
            if (shorter.length >= 6 && longer.includes(shorter)) {
                hit = true;
            }
            // Rule 2: shared 2-token prefix + one extends the other
            if (!hit) {
                const aT = a._tokens;
                const bT = b._tokens;
                if (
                    aT.length >= 2 &&
                    bT.length >= 2 &&
                    aT[0] === bT[0] &&
                    aT[1] === bT[1] &&
                    aT.length !== bT.length
                ) {
                    hit = true;
                }
            }
            // Rule 3: same string with whitespace removed
            if (!hit) {
                if (
                    a._collapsed.length >= 6 &&
                    a._collapsed === b._collapsed &&
                    a._k !== b._k
                ) {
                    hit = true;
                }
            }

            if (hit) {
                if (!map.has(a.vendor_key)) map.set(a.vendor_key, b);
                break;
            }
        }
    }
    return map;
}

/* Client-side sorter for the vendor list. Backend returns them in default
   activity order; this lets the user re-sort without a refetch. */
function sortVendors(list, key) {
    if (!Array.isArray(list)) return list;
    const arr = [...list];
    const num = (v) => Number(v || 0);
    switch (key) {
        case 'billed':
            arr.sort((a, b) => num(b.total_amount) - num(a.total_amount));
            break;
        case 'outstanding':
            arr.sort((a, b) =>
                (num(b.total_amount) - num(b.total_paid)) - (num(a.total_amount) - num(a.total_paid)),
            );
            break;
        case 'bills':
            arr.sort((a, b) => num(b.bill_count) - num(a.bill_count));
            break;
        case 'name':
            arr.sort((a, b) => (a.vendor || '').localeCompare(b.vendor || ''));
            break;
        case 'activity':
        default:
            arr.sort((a, b) => (b.last_activity || '').localeCompare(a.last_activity || ''));
    }
    return arr;
}

export default function VendorsPage() {
    const [rows, setRows] = useState(null);
    const [period, setPeriod] = useSharedPeriod();
    const [sortBy, setSortBy] = useState('activity'); // activity | billed | outstanding | name | bills

    useEffect(() => {
        setRows(null);
        const params = periodToParams(period);
        api.listVendors(params).then((r) => setRows(sortVendors(r, sortBy))).catch(() => setRows([]));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [period?.from, period?.to, period?.preset, sortBy]);

    // Phase 13 — fetch all reconciliations so we can show a status badge per vendor
    const [recs, setRecs] = useState({});
    useEffect(() => {
        let cancelled = false;
        api.listReconciliations()
            .then((r) => {
                if (cancelled) return;
                const map = {};
                for (const x of r.reconciliations || []) map[x.vendor_key] = x;
                setRecs(map);
            })
            .catch(() => setRecs({}));
        return () => { cancelled = true; };
    }, [rows]);

    const similarMap = useMemo(() => computeSimilar(rows || []), [rows]);

    return (
        <div className="space-y-5 animate-fade-in">
            <PageHeader
                title="Vendors"
                subtitle={
                    period && period.preset !== 'all'
                        ? `Vendors that billed you in ${period.label}.`
                        : 'Total spend, bill count and last activity per vendor. Click a vendor to open their account view.'
                }
                actions={
                    <div className="flex items-center gap-2">
                        <select
                            value={sortBy}
                            onChange={(e) => setSortBy(e.target.value)}
                            className="h-10 rounded-md border border-input bg-background px-3 text-sm"
                            data-testid="vendors-sort"
                        >
                            <option value="activity">Sort: Last activity</option>
                            <option value="billed">Sort: Billed (high → low)</option>
                            <option value="outstanding">Sort: Outstanding (high → low)</option>
                            <option value="bills">Sort: Bill count</option>
                            <option value="name">Sort: Name (A → Z)</option>
                        </select>
                        <PeriodPicker value={period} onChange={setPeriod} testid="vendors-period-picker" />
                    </div>
                }
            />
            <Card className="p-0 border-border/80 overflow-hidden">
                {rows === null ? (
                    <div className="p-4 space-y-2">
                        {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className="h-10" />)}
                    </div>
                ) : rows.length === 0 ? (
                    <div className="p-10 text-center">
                        <div className="mx-auto h-10 w-10 rounded-full bg-accent text-accent-foreground flex items-center justify-center">
                            <Store size={20} />
                        </div>
                        <div className="font-heading text-lg mt-3">No vendors yet</div>
                        <div className="text-sm text-muted-foreground">Upload a bill to see vendors here.</div>
                    </div>
                ) : (
                    <TooltipProvider delayDuration={200}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm" data-testid="vendors-table">
                                <thead className="bg-muted/40 text-muted-foreground">
                                    <tr>
                                        <th className="text-left px-4 py-2 font-medium">Vendor</th>
                                        <th className="text-right px-4 py-2 font-medium">Bills</th>
                                        <th className="text-right px-4 py-2 font-medium">Billed</th>
                                        <th className="text-right px-4 py-2 font-medium">Paid</th>
                                        <th className="text-right px-4 py-2 font-medium">Outstanding</th>
                                        <th className="text-left px-4 py-2 font-medium">Last activity</th>
                                        <th className="text-right px-4 py-2 font-medium">Open</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.map((v) => {
                                        const similar = similarMap.get(v.vendor_key);
                                        const rec = recs[v.vendor_key];
                                        const outstanding = Number(v.outstanding || 0);
                                        const outCls =
                                            outstanding > 0
                                                ? 'bg-[hsl(var(--price-increase-bg))] text-[hsl(var(--price-increase-fg))] border border-[hsl(var(--price-increase-border))]'
                                                : outstanding < 0
                                                    ? 'bg-[hsl(var(--price-decrease-bg))] text-[hsl(var(--price-decrease-fg))] border border-[hsl(var(--price-decrease-border))]'
                                                    : 'bg-muted text-muted-foreground border border-border';
                                        return (
                                            <tr key={v.vendor_key} className="border-t border-border hover:bg-muted/40">
                                                <td className="px-4 py-2">
                                                    <div className="flex items-center gap-2">
                                                        <Link
                                                            to={`/vendors/${encodeURIComponent(v.vendor_key)}`}
                                                            data-testid={`vendors-name-${v.vendor_key}`}
                                                            className="font-medium hover:underline uppercase tracking-wide"
                                                        >
                                                            {v.vendor}
                                                        </Link>
                                                        {rec && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Link
                                                                        to={`/vendors/${encodeURIComponent(v.vendor_key)}`}
                                                                        className={
                                                                            'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ' +
                                                                            (rec.status === 'match'
                                                                                ? 'bg-emerald-50 dark:bg-emerald-950 text-emerald-700 dark:text-emerald-400 border border-emerald-300 dark:border-emerald-800'
                                                                                : 'bg-amber-50 dark:bg-amber-950 text-amber-800 dark:text-amber-300 border border-amber-300 dark:border-amber-800')
                                                                        }
                                                                        data-testid={`vendors-recon-${v.vendor_key}`}
                                                                    >
                                                                        {rec.status === 'match' ? (
                                                                            <>
                                                                                <ClipboardCheck size={10} /> tallied
                                                                            </>
                                                                        ) : (
                                                                            <>
                                                                                <AlertTriangle size={10} /> diff ₹{Math.abs(rec.diff || 0).toLocaleString('en-IN', {maximumFractionDigits: 0})}
                                                                            </>
                                                                        )}
                                                                    </Link>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top" className="max-w-xs">
                                                                    <div className="text-xs space-y-1">
                                                                        <div className="font-medium">{rec.message || 'Reconciliation'}</div>
                                                                        {rec.hint && <div className="text-muted-foreground">{rec.hint}</div>}
                                                                        <div className="text-muted-foreground">
                                                                            Ledger: {fmtMoney(rec.ledger_outstanding ?? rec.closing_balance, rec.currency)} · Ours: {fmtMoney(rec.outstanding_by_us, rec.currency)}
                                                                        </div>
                                                                    </div>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                        {similar && (
                                                            <Tooltip>
                                                                <TooltipTrigger asChild>
                                                                    <Link
                                                                        to={`/vendors/${encodeURIComponent(v.vendor_key)}`}
                                                                        className="inline-flex items-center gap-1 rounded-full border border-[hsl(var(--warning-border))] bg-[hsl(var(--warning-bg))] text-[hsl(var(--warning-fg))] px-2 py-0.5 text-[10px] uppercase tracking-wide"
                                                                        data-testid={`vendors-similar-hint-${v.vendor_key}`}
                                                                    >
                                                                        <Sparkles size={10} /> looks similar
                                                                    </Link>
                                                                </TooltipTrigger>
                                                                <TooltipContent side="top" align="start" className="max-w-xs">
                                                                    <div className="text-xs">
                                                                        <div className="font-medium mb-1">
                                                                            Possible duplicate of “{similar.vendor}”
                                                                        </div>
                                                                        <div className="text-muted-foreground">
                                                                            Open this vendor and use{' '}
                                                                            <b>Rename / Merge</b> to combine them.
                                                                        </div>
                                                                    </div>
                                                                </TooltipContent>
                                                            </Tooltip>
                                                        )}
                                                    </div>
                                                </td>
                                                <td className="px-4 py-2 text-right tabular-nums font-mono">{v.bills_count}</td>
                                                <td className="px-4 py-2 text-right tabular-nums font-mono">{fmtMoney(v.total_spend)}</td>
                                                <td className="px-4 py-2 text-right tabular-nums font-mono text-muted-foreground">
                                                    {fmtMoney(v.total_paid || 0)}
                                                </td>
                                                <td className="px-4 py-2 text-right">
                                                    <span
                                                        className={`inline-block rounded-full px-2 py-0.5 text-xs tabular-nums font-mono ${outCls}`}
                                                        data-testid={`vendors-outstanding-${v.vendor_key}`}
                                                    >
                                                        {outstanding === 0
                                                            ? 'Settled'
                                                            : outstanding > 0
                                                                ? fmtMoney(outstanding)
                                                                : `-${fmtMoney(Math.abs(outstanding))}`}
                                                    </span>
                                                </td>
                                                <td className="px-4 py-2 text-muted-foreground">{fmtDateTime(v.last_bill_date)}</td>
                                                <td className="px-4 py-2 text-right">
                                                    <Link
                                                        to={`/vendors/${encodeURIComponent(v.vendor_key)}`}
                                                        className="text-primary hover:underline text-xs inline-flex items-center gap-1"
                                                        data-testid={`vendors-open-${v.vendor_key}`}
                                                    >
                                                        account view <ArrowRight size={12} />
                                                    </Link>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    </TooltipProvider>
                )}
            </Card>
        </div>
    );
}
